import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Features from '../../constants/features'
import { getAppointmentTypesMap } from '../../store/duck/appointmentTypes'
import { getClientType, getCurrentClient } from '../../store/duck/clients'
import { getFeatureToggle } from '../../store/duck/constants'
// @ts-ignore
import { getCurrentPatient } from '../../store/duck/patients'
import {
  createAppointment,
  editAppointment,
  getCurrentAppointment,
  getIsFetchingStuff,
  getSchedulesIsLoading,
} from '../../store/duck/schedules'
import {
  Appointment,
  AppointmentConfig,
  AppointmentToCreateFromStore,
  BusinessAppointment,
  BusinessAppointmentToEditFromStore,
  BusinessAppointmentType,
} from '../../types/entities/appointments'
import { KioskPatient } from '../../types/entities/patients'
// @ts-ignore
import { useNavigateWithQueryString } from '../../utils'
// @ts-ignore
import useCallbackWhenLoaded from '../../utils/useCallbackWhenLoaded'
import CalendarSection, {
  CalendarSectionHandle,
} from '../common/calendar-section/CalendarSection'
import KioskScreen from './KioskScreen'

const KioskCalendarScreen = () => {
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const navigate = useNavigate()
  const { t } = useTranslation('Common')

  const appointment = useSelector(getCurrentAppointment) as
    | Appointment
    | AppointmentToCreateFromStore
  const isLoading = useSelector(getSchedulesIsLoading)
  const isFetchingStuff = useSelector(getIsFetchingStuff)
  const appointmentTypesMap = useSelector(getAppointmentTypesMap)
  const clientType = useSelector(getClientType)
  const client = useSelector(getCurrentClient)
  const patient = useSelector(getCurrentPatient) as KioskPatient | null
  const isNewFlowEnabled = useSelector(
    getFeatureToggle(Features.NEW_APPOINTMENT_BOOKING_FLOW_KIOSK),
  )

  const calendarSectionRef = useRef<CalendarSectionHandle>(null)

  const [canProceed, setCanProceed] = useState(false)

  const isCreate = !(appointment as Appointment)?.id

  const onAppointmentCreated = () => {
    if (isNewFlowEnabled && clientType === 'new') {
      navigateWithQueryString({ url: '/contact-confirmation' })
    } else {
      navigateWithQueryString({ url: '/any-more-pets' })
    }
  }

  const callbackWhenLoaded = useCallbackWhenLoaded(
    onAppointmentCreated,
    getSchedulesIsLoading,
  )

  const finalize = (config: AppointmentConfig) => {
    callbackWhenLoaded()

    if (isCreate) {
      const newAppointment = {
        ...(appointment as AppointmentToCreateFromStore),
        ...config,
        type: appointmentTypesMap[appointment!.type as string]?.eventTypeId,
        businessAppointmentType: { id: appointment!.type as string },
      }
      dispatch(createAppointment(newAppointment))
    } else {
      const getTypes = () => {
        const isTypeEdited = typeof appointment.type === 'string'
        if (isTypeEdited) {
          return {
            eventTypeId:
              appointmentTypesMap[appointment.type as string]?.eventTypeId,
            businessAppointmentTypeId: appointment.type as string,
          }
        }
        return {
          eventTypeId: (appointment as Appointment).type.id,
          businessAppointmentTypeId: (appointment as BusinessAppointment)
            .businessAppointmentType.id,
        }
      }

      const { eventTypeId, businessAppointmentTypeId } = getTypes()

      const newAppointmentForEdit = {
        ...(appointment as BusinessAppointmentToEditFromStore),
        ...config,
        type: eventTypeId,
        businessAppointmentType: {
          id: businessAppointmentTypeId as string,
        } as BusinessAppointmentType,
      }

      const editedAppointment = {
        ...newAppointmentForEdit,
        client: newAppointmentForEdit.client.id,
        patient: newAppointmentForEdit.patient.id,
        state: newAppointmentForEdit.state.id,
      }

      const editedV2Appointment = {
        ...newAppointmentForEdit,
        client: client?.id
          ? client.id
          : typeof newAppointmentForEdit.client === 'object'
          ? newAppointmentForEdit.client.id
          : newAppointmentForEdit.client,
        patient: patient?.id
          ? patient.id
          : typeof newAppointmentForEdit.patient === 'object'
          ? newAppointmentForEdit.patient.id
          : newAppointmentForEdit.patient,
        state:
          typeof newAppointmentForEdit.state === 'object'
            ? newAppointmentForEdit.state.id
            : newAppointmentForEdit.state,
      }
      dispatch(
        editAppointment(
          isNewFlowEnabled ? editedV2Appointment : editedAppointment,
        ),
      )
    }
  }

  const handleBack = () => {
    if (isNewFlowEnabled && clientType === 'new') {
      navigateWithQueryString({ url: '/visit-reason' })
    } else {
      calendarSectionRef.current?.onBack()
    }
  }

  return (
    <KioskScreen
      justifyContent="flex-start"
      proceedButtonDisabled={!canProceed}
      proceedButtonLabel={
        isCreate ? t('Common:CREATE_APPOINTMENT') : t('Common:EDIT_APPOINTMENT')
      }
      proceedButtonLoading={isLoading && !isFetchingStuff}
      onBack={handleBack}
      onProceed={calendarSectionRef.current?.onProceed}
    >
      <CalendarSection
        appointment={appointment}
        ref={calendarSectionRef}
        onBack={() => navigate(-1)}
        onCanProceedChange={setCanProceed}
        onFinalize={finalize}
      />
    </KioskScreen>
  )
}

export default KioskCalendarScreen
